/**
 * Chunk加载失败处理工具
 * 解决生产环境代码分割导致的加载失败问题
 */

// 重试次数限制
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY = 1000; // 重试延迟(毫秒)

// 存储每个路由的重试次数
const retryCount = new Map();

/**
 * 检查是否为chunk加载失败错误
 */
function isChunkLoadError(error) {
  const message = error.message || '';
  return (
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('Failed to fetch') ||
    message.includes('net::ERR_CACHE_READ_FAILURE') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * 获取当前路由的重试次数
 */
function getRetryCount(route) {
  return retryCount.get(route) || 0;
}

/**
 * 增加重试次数
 */
function incrementRetryCount(route) {
  const count = getRetryCount(route) + 1;
  retryCount.set(route, count);
  return count;
}

/**
 * 重置重试次数
 */
function resetRetryCount(route) {
  retryCount.delete(route);
}

/**
 * 清理缓存并重新加载页面
 */
function clearCacheAndReload() {
  console.warn('清理缓存并重新加载页面...');
  
  // 清理各种缓存
  if ('caches' in window) {
    caches.keys().then(function(names) {
      for (let name of names) {
        caches.delete(name);
      }
    });
  }
  
  // 强制刷新页面(绕过缓存)
  window.location.reload(true);
}

/**
 * 处理chunk加载失败
 */
export function handleChunkError(error, route = window.location.pathname) {
  console.error('Chunk加载失败:', error);
  
  if (!isChunkLoadError(error)) {
    throw error; // 非chunk错误，继续抛出
  }
  
  const currentRetryCount = incrementRetryCount(route);
  console.warn(`路由 ${route} chunk加载失败，第 ${currentRetryCount} 次重试`);
  
  if (currentRetryCount >= MAX_RETRY_COUNT) {
    console.error(`路由 ${route} 超过最大重试次数，清理缓存并重新加载页面`);
    resetRetryCount(route);
    clearCacheAndReload();
    return;
  }
  
  // 延迟重试
  setTimeout(() => {
    console.log(`正在重试加载路由 ${route}...`);
    window.location.reload();
  }, RETRY_DELAY * currentRetryCount);
}

/**
 * 路由成功加载后重置重试次数
 */
export function onRouteSuccess(route = window.location.pathname) {
  resetRetryCount(route);
}

/**
 * Vue Router错误处理
 */
export function setupRouterErrorHandler(router) {
  // 监听路由错误
  router.onError((error) => {
    console.error('Router错误:', error);
    handleChunkError(error);
  });
  
  // 监听路由成功
  router.afterEach((to) => {
    onRouteSuccess(to.path);
  });
}

/**
 * 懒加载组件包装器
 * 为动态导入添加错误处理
 */
export function lazyComponent(importFunc) {
  return () => importFunc().catch(error => {
    console.error('组件懒加载失败:', error);
    handleChunkError(error);
    throw error;
  });
}

/**
 * 全局错误处理配置
 */
export function setupGlobalErrorHandler() {
  // Vue全局错误处理
  Vue.config.errorHandler = function (err, vm, info) {
    console.error('Vue全局错误:', err, info);
    if (isChunkLoadError(err)) {
      handleChunkError(err);
    }
  };
  
  // 全局未捕获异常处理
  window.addEventListener('error', (event) => {
    console.error('全局错误:', event.error);
    if (isChunkLoadError(event.error)) {
      event.preventDefault();
      handleChunkError(event.error);
    }
  });
  
  // 全局未处理的Promise异常
  window.addEventListener('unhandledrejection', (event) => {
    console.error('未处理的Promise异常:', event.reason);
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      handleChunkError(event.reason);
    }
  });
}

export default {
  handleChunkError,
  onRouteSuccess,
  setupRouterErrorHandler,
  lazyComponent,
  setupGlobalErrorHandler
}; 


// WEBPACK FOOTER //
// ./src/utils/chunkErrorHandler.js